/**
 * Gallery (autonomous) API layer.
 *
 * The widget has been converted into a self-contained screen gallery: there is
 * NO backend. Every function below preserves the original public signature and
 * return type, but resolves with static mock data so the existing pages and
 * hooks (useClaimLink, useQuote, useExchangeRate, ...) keep working untouched.
 */
import type { IWidgetSession } from '../shared/session/interfaces/session';
import { LINK_ERROR_CODE } from '../utils/commons';
import {
  buildMockExchangeRate,
  buildMockQuote,
  mockSession,
} from '../gallery/mockData';

/**
 * Error class kept for type-compatibility with the original claim flow.
 */
export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number,
  ) {
    super(message);
    this.name = 'LinkClaimError';
  }
}

/**
 * Kept so existing listeners (useSessionExpiredHandler) still compile.
 * It is never dispatched in gallery mode.
 */
export const SESSION_EXPIRED_EVENT = 'm2m-session-expired';

/** No-op in gallery mode. */
export const setWidgetLinkId = (_linkId: string | null) => {
  void _linkId;
};

/** Small helper to mimic async latency so loaders/transitions are visible. */
const delay = <T>(value: T, ms = 250): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

export const claimLink = async (
  linkId: string,
  _deviceInfo?: IDeviceInfo,
): Promise<IWidgetSession> => {
  void _deviceInfo;
  return delay({ ...mockSession, linkId });
};

export const getSession = async (linkId: string): Promise<IWidgetSession> => {
  return delay({ ...mockSession, linkId });
};

// ============================================
// Operations API (Exchange Rate, Quote, Cards)
// ============================================

export interface IExchangeRateResponse {
  sourceCurrency: string;
  destinationCurrency: string;
  rate: number;
  timestamp: string;
}

export const getExchangeRate = async (
  sourceCurrency: string,
  destinationCurrency: string,
): Promise<IExchangeRateResponse> => {
  return delay(buildMockExchangeRate(sourceCurrency, destinationCurrency));
};

export interface IQuoteRequest {
  sourceCountry: string;
  destinationCountry: string;
  fundingMethod: string;
  payoutMethod: string;
  amount: number;
  currency: string;
  cardId?: string;
}

export interface IQuoteResponse {
  quoteId: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod: string;
  payoutMethod: string;
  expiresAt: string;
  signedQuote: string;
  cardId?: string;
}

export const getQuote = async (
  request: IQuoteRequest,
): Promise<IQuoteResponse> => {
  const quote = buildMockQuote(request.amount);
  return delay({
    ...quote,
    sourceCurrency: request.currency,
    fundingMethod: request.fundingMethod,
    payoutMethod: request.payoutMethod,
    cardId: request.cardId ?? quote.cardId,
  });
};

export interface IStoredCard {
  cardId: string;
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName?: string;
  isDefault: boolean;
}

export interface IAddCardRequest {
  token: string;
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName?: string;
}

export const addCard = async (
  _linkId: string,
  card: IAddCardRequest,
): Promise<IStoredCard> => {
  void _linkId;
  return delay({
    cardId: `card_${card.brand.toLowerCase()}_${card.last4}`,
    last4: card.last4,
    brand: card.brand,
    expiryMonth: card.expiryMonth,
    expiryYear: card.expiryYear,
    cardholderName: card.cardholderName,
    isDefault: true,
  });
};

export const listCards = async (_linkId: string): Promise<IStoredCard[]> => {
  void _linkId;
  return delay([]);
};

export const removeCard = async (
  _linkId: string,
  _cardId: string,
): Promise<void> => {
  void _linkId;
  void _cardId;
  return delay(undefined);
};

// ============================================
// Operations Create API
// ============================================

export interface ICreateOperationRequest {
  signedQuote: string;
  notes?: string;
}

export interface IOperationResponse {
  operationId: string;
  quoteId: string;
  status: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingCardId: string;
  notes?: string;
  createdAt: string;
  cancellation?: {
    allowed: boolean;
    mode?: 'void' | 'request';
    expiresAt?: string;
    unavailableReason?: string;
    requestedAt?: string;
  };
}

const buildMockOperation = (): IOperationResponse => {
  const quote = buildMockQuote();
  return {
    operationId: 'op-demo',
    quoteId: quote.quoteId,
    status: 'completed',
    sendAmount: quote.sendAmount,
    receiveAmount: quote.receiveAmount,
    exchangeRate: quote.exchangeRate,
    fee: quote.fee,
    totalCost: quote.totalCost,
    sourceCurrency: quote.sourceCurrency,
    destinationCurrency: quote.destinationCurrency,
    fundingCardId: quote.cardId ?? 'card_visa_demo',
    createdAt: new Date().toISOString(),
  };
};

export const createOperation = async (
  _linkId: string,
  _request: ICreateOperationRequest,
): Promise<IOperationResponse> => {
  void _linkId;
  void _request;
  return delay(buildMockOperation());
};

export const listOperations = async (
  _linkId: string,
): Promise<IOperationResponse[]> => {
  void _linkId;
  return delay([
    { ...buildMockOperation(), operationId: 'op-1', status: 'completed' },
    { ...buildMockOperation(), operationId: 'op-2', status: 'pending' },
  ]);
};

// ============================================
// Legal documents API (mocked for the gallery)
// ============================================

export interface ILegalDocumentResponse {
  doc: string;
  lang: string;
  html: string;
  fetchedAt: string;
  fromCache: boolean;
}

/**
 * Gallery-safe legal document loader.
 *
 * The production widget fetches each legal document's HTML from the backend
 * proxy (`GET /widget/legal/:doc/:lang`). For the autonomous gallery there is no
 * backend, so we resolve with static placeholder HTML that keeps the same shape
 * and styling hooks the real documents use.
 */
const buildMockLegalHtml = (doc: string, lang: string): string => {
  const isEs = lang.startsWith('es');
  const heading = isEs ? 'Documento de ejemplo' : 'Sample document';
  const intro = isEs
    ? 'Este es un documento legal de ejemplo mostrado en el screenbook. En producción, el contenido real se obtiene desde el backend.'
    : 'This is a sample legal document rendered inside the screenbook. In production the real content is fetched from the backend.';
  const sectionTitle = isEs ? 'Sección' : 'Section';
  const body = isEs
    ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent commodo, sapien nec lacinia tincidunt, nisl nibh ultricies arcu, vitae aliquet libero massa eget velit.'
    : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent commodo, sapien nec lacinia tincidunt, nisl nibh ultricies arcu, vitae aliquet libero massa eget velit.';

  return `
    <h1>${heading}: ${doc}</h1>
    <p>${intro}</p>
    <h2>1. ${sectionTitle}</h2>
    <p>${body}</p>
    <h2>2. ${sectionTitle}</h2>
    <p>${body}</p>
    <ul>
      <li>${body}</li>
      <li>${body}</li>
      <li>${body}</li>
    </ul>
    <h2>3. ${sectionTitle}</h2>
    <p>${body}</p>
    <p>${body}</p>
  `;
};

export const getLegalDocument = async (
  doc: string,
  lang: string,
): Promise<ILegalDocumentResponse> => {
  return delay({
    doc,
    lang,
    html: buildMockLegalHtml(doc, lang),
    fetchedAt: new Date().toISOString(),
    fromCache: false,
  });
};
